/**
 * OrderSummaryCard — sticky right-rail used by both Checkout pages.
 *
 * Shows items (with thumbnails), itemised totals, and the grand total.
 * Collapsable on mobile so it doesn't dwarf the form.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, css } from '../../design';
import SmartImage from '../SmartImage';
import { Product } from '../../api/products';

export interface SummaryLine {
  productId: number;
  name: string;
  quantity: number;
  price: number;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
}

export interface OrderSummaryCardProps {
  storeName: string;
  storeAddress?: string;
  items: SummaryLine[];
  subtotal: number;
  deliveryFee: number;
  discountAmount: number;
  tipAmount?: number;
  total: number;
  paymentMethodLabel?: string;
  /** Optional product map to enrich items missing thumbnails. */
  productsMap?: Map<number, Product>;
}

const OrderSummaryCard: React.FC<OrderSummaryCardProps> = ({
  storeName,
  storeAddress,
  items,
  subtotal,
  deliveryFee,
  discountAmount,
  tipAmount = 0,
  total,
  paymentMethodLabel,
  productsMap,
}) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Card padding="none" style={{ overflow: 'hidden', position: 'sticky', top: 'calc(var(--pm-navbar-height, 88px) + 12px)' }}>
      <div
        style={{
          background: `linear-gradient(135deg, ${css.color.primary}, ${css.color.primaryStrong})`,
          padding: '12px 16px',
          color: '#FFFFFF',
        }}
      >
        <div style={{ fontSize: 12, opacity: 0.8 }}>Order from</div>
        <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' }}>{storeName}</div>
        {storeAddress && (
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{storeAddress}</div>
        )}
      </div>

      <div style={{ padding: 14 }}>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            marginBottom: expanded ? 8 : 0,
            fontWeight: 700,
            color: css.color.text,
            fontSize: 13,
          }}
        >
          <span>{items.length} item{items.length === 1 ? '' : 's'}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: css.color.textMuted, fontSize: 12, fontWeight: 600 }}>
            {expanded ? <>Hide <ChevronUp size={14} /></> : <>Show <ChevronDown size={14} /></>}
          </span>
        </button>

        {expanded && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 10, borderBottom: `1px solid ${css.color.border}` }}>
            {items.map((it) => {
              const p = productsMap?.get(it.productId);
              return (
                <div key={it.productId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: css.color.surface2 }}>
                    <SmartImage
                      src={it.imageUrl || p?.image_url}
                      thumb={it.thumbnailUrl || p?.thumbnail_url}
                      alt={it.name}
                      radius={6}
                      size="thumb"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: css.color.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {it.name}
                    </div>
                    <div style={{ fontSize: 11, color: css.color.textMuted }}>
                      Qty: {it.quantity} · ₹{it.price.toFixed(0)} each
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: css.color.text, fontVariantNumeric: 'tabular-nums' }}>
                    ₹{(it.price * it.quantity).toFixed(0)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 10, fontSize: 13, color: css.color.text2 }}>
          <Row label="Subtotal" value={`₹${subtotal.toFixed(0)}`} />
          <Row label="Delivery fee" value={deliveryFee === 0 ? 'FREE' : `₹${deliveryFee.toFixed(0)}`} valueColor={deliveryFee === 0 ? css.color.primary : undefined} />
          {tipAmount > 0 && <Row label="Delivery tip" value={`₹${tipAmount.toFixed(0)}`} />}
          {discountAmount > 0 && <Row label="Discount" value={`-₹${discountAmount.toFixed(0)}`} valueColor={css.color.primary} />}
        </div>

        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: `1px dashed ${css.color.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 800,
            color: css.color.text,
            fontFamily: css.font.display,
          }}
        >
          <span style={{ fontSize: 15 }}>Total</span>
          <span style={{ fontSize: 20, fontVariantNumeric: 'tabular-nums', color: css.color.primary }}>
            ₹{total.toFixed(0)}
          </span>
        </div>

        {paymentMethodLabel && (
          <div style={{ marginTop: 10, fontSize: 12, color: css.color.textMuted }}>
            Paying via <strong style={{ color: css.color.text }}>{paymentMethodLabel}</strong>
          </div>
        )}
      </div>
    </Card>
  );
};

const Row: React.FC<{ label: string; value: string; valueColor?: string }> = ({ label, value, valueColor }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
    <span>{label}</span>
    <span style={{ fontWeight: 700, color: valueColor ?? css.color.text, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
  </div>
);

export default OrderSummaryCard;

